/**
 * CartManager — клиентский модуль корзины.
 *
 * localStorage хранит минимум: [{product_id, variation_id, quantity}]
 * Общается с REST API: POST /kg/v1/cart/validate
 * Событие cart:updated (CustomEvent на document) для обновления UI.
 */
(function () {
  'use strict';

  if (!window.kgCart) {
    console.warn('Cart: kgCart localize object not found');
    return;
  }

  const STORAGE_KEY = 'kg_cart';
  const MAX_QUANTITY = 9999;
  const formatPrice = (n) => n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';

  class CartManager {
    constructor() {
      this._lastEnriched = [];
      this._lastTotal = 0;
      this._syncController = null;
      this._syncDebounceTimer = null;
    }

    // localStorage

    _read() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        console.warn('Cart: localStorage read error', e);
        return [];
      }
    }

    _write(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        console.warn('Cart: localStorage write error', e);
      }
      this._dispatchUpdate();
    }

    /**
     * Нормализует variation_id — всегда строка.
     * У каждого товара есть хотя бы одна вариация с ключом.
     */
    _vid(variation_id) {
      return variation_id ? String(variation_id) : '';
    }

    _makeKey(product_id, variation_id) {
      return product_id + '|' + this._vid(variation_id);
    }

    // Публичные методы

    /**
     * Добавить товар. Если такой product_id+variation_id уже есть — quantity складывается.
     */
    add(product_id, variation_id, quantity) {
      product_id = parseInt(product_id, 10);
      variation_id = this._vid(variation_id);
      quantity = Math.min(MAX_QUANTITY, Math.max(1, parseInt(quantity, 10) || 1));

      const items = this._read();
      const key = this._makeKey(product_id, variation_id);
      let found = false;

      for (let i = 0; i < items.length; i++) {
        if (this._makeKey(items[i].product_id, items[i].variation_id) === key) {
          items[i].quantity = Math.min(MAX_QUANTITY, items[i].quantity + quantity);
          found = true;
          break;
        }
      }

      if (!found) {
        items.push({ product_id, variation_id, quantity });
      }

      this._write(items);
    }

    /**
     * Обновить количество конкретной позиции.
     * quantity <= 0 — удаляет позицию.
     */
    update(product_id, variation_id, quantity) {
      product_id = parseInt(product_id, 10);
      variation_id = this._vid(variation_id);
      quantity = parseInt(quantity, 10) || 0;

      if (quantity <= 0) {
        return this.remove(product_id, variation_id);
      }

      quantity = Math.min(MAX_QUANTITY, quantity);

      const items = this._read();
      const key = this._makeKey(product_id, variation_id);

      for (let i = 0; i < items.length; i++) {
        if (this._makeKey(items[i].product_id, items[i].variation_id) === key) {
          items[i].quantity = quantity;
          break;
        }
      }

      this._write(items);
    }

    /**
     * Удалить позицию из корзины.
     */
    remove(product_id, variation_id) {
      product_id = parseInt(product_id, 10);
      variation_id = this._vid(variation_id);
      const key = this._makeKey(product_id, variation_id);

      const items = this._read().filter(
        (item) => this._makeKey(item.product_id, item.variation_id) !== key
      );

      this._write(items);
    }

    /** Получить текущие items из localStorage (без запроса). */
    getItems() {
      return this._read();
    }

    /** Количество позиций (строк) в корзине — для badge. */
    getCount() {
      return this._read().length;
    }

    /** Суммарное количество единиц товара (все quantity). */
    getTotalQuantity() {
      return this._read().reduce((sum, item) => sum + (item.quantity || 0), 0);
    }

    /** Очистить корзину. */
    clear() {
      this._lastEnriched = [];
      this._lastTotal = 0;
      this._write([]);
    }

    /** Последние enriched-данные (после sync). */
    getEnriched() {
      return this._lastEnriched;
    }

    /** Последний total (после sync). */
    getTotal() {
      return this._lastTotal;
    }

    // Sync

    /**
     * Синхронизация с сервером: POST /kg/v1/cart/validate
     * Обновляет localStorage очищенным массивом, сохраняет enriched + total.
     * Возвращает Promise.
     */
    sync() {
      const items = this._read().filter(
        (item) => item && item.product_id && item.quantity > 0
      );

      if (!items.length) {
        this._lastEnriched = [];
        this._lastTotal = 0;
        this._dispatchUpdate();
        return Promise.resolve({ enriched: [], items: [], total: 0 });
      }

      // Abort предыдущего запроса
      if (this._syncController) {
        this._syncController.abort();
      }
      this._syncController = new AbortController();

      return fetch(kgCart.rest_url + 'kg/v1/cart/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': kgCart.nonce,
        },
        body: JSON.stringify({ items }),
        signal: this._syncController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('HTTP ' + response.status);
          }
          return response.json();
        })
        .then((resp) => {
          this._syncController = null;

          if (!resp || !resp.success) {
            this._dispatchUpdate();
            return resp;
          }

          try {
            // Мержим: серверный ответ + позиции, добавленные пока шёл запрос
            const currentItems = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
            const requestKeys = new Set(
              items.map((i) => i.product_id + '|' + (i.variation_id || ''))
            );

            // Позиции, добавленные после отправки запроса (их нет в запросе)
            const newItems = currentItems.filter((i) => {
              const key = i.product_id + '|' + (i.variation_id || '');
              return !requestKeys.has(key);
            });

            const merged = [...(resp.items || []), ...newItems];
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          } catch (e) {
            console.warn('Cart: localStorage write error after sync', e);
          }

          this._lastEnriched = resp.enriched || [];
          this._lastTotal = resp.total || 0;

          this._dispatchUpdate();
          return resp;
        })
        .catch((err) => {
          this._syncController = null;
          if (err.name !== 'AbortError') {
            console.warn('Cart: sync error', err);
            this._dispatchUpdate();
            throw err;
          }
        });
    }

    /** sync с debounce (для +/- количества). */
    syncDebounced(delay = 600) {
      clearTimeout(this._syncDebounceTimer);
      this._syncDebounceTimer = setTimeout(() => this.sync(), delay);
    }

    // Event

    _dispatchUpdate() {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            count: this.getCount(),
            total: this._lastTotal,
            enriched: this._lastEnriched,
          },
        })
      );
    }

    // Синхронизация между вкладками

    _initCrossTabSync() {
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY) {
          this._dispatchUpdate();
        }
      });
    }
  }

  // кнопка «В корзину»

  function initAddToCartButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-addToCart');
      if (!btn || btn.disabled) return;

      e.preventDefault();

      const container = btn.closest('.js-productWidget, .js-productCard');

      let productId = btn.getAttribute('data-product-id');
      let variationId = btn.getAttribute('data-variation-id');
      let quantity = parseInt(btn.getAttribute('data-quantity'), 10) || 1;

      if (container) {
        const activeType = container.querySelector(
          '.product-detail-purchase__type--active, .product-card__type--active'
        );
        if (activeType && activeType.getAttribute('data-variation-id')) {
          variationId = activeType.getAttribute('data-variation-id');
        }

        const qtyInput = container.querySelector('.js-pqInput');
        if (qtyInput) {
          const inputVal = parseInt(qtyInput.value, 10);
          if (inputVal > 0) {
            quantity = inputVal;
          }
        }
      }

      if (!productId) return;

      window.KG.cart.add(productId, variationId, quantity);
      window.KG.cart.syncDebounced();

      const labelEl = btn.querySelector('.js-addToCart-label');
      const addedEl = btn.querySelector('.js-addToCart-added');

      if (labelEl && addedEl) {
        labelEl.style.display = 'none';
        addedEl.style.display = '';
        btn.disabled = true;

        const addedVariationId = variationId || '';

        setTimeout(() => {
          const wrap = btn.closest('.js-addToCartWrap');
          if (!wrap) return;

          const currentContainer = btn.closest('.js-productWidget, .js-productCard');
          let currentVid = btn.getAttribute('data-variation-id') || '';
          if (currentContainer) {
            const activeType = currentContainer.querySelector(
              '.product-detail-purchase__type--active, .product-card__type--active'
            );
            if (activeType && activeType.getAttribute('data-variation-id')) {
              currentVid = activeType.getAttribute('data-variation-id');
            }
          }

          if (currentVid !== addedVariationId) {
            labelEl.style.display = '';
            addedEl.style.display = 'none';
            btn.disabled = false;
            return;
          }

          btn.style.display = 'none';
          const link = wrap.querySelector('.js-inCartLink');
          if (link) link.style.display = '';
        }, 350);
      }
    });
  }

  function syncCartButtons() {
    const items = window.KG.cart.getItems();
    const inCartKeys = new Set(
      items.map((item) => String(item.product_id) + '|' + (item.variation_id || ''))
    );

    document.querySelectorAll('.js-addToCartWrap').forEach((wrap) => {
      const pid = wrap.getAttribute('data-product-id');
      const btn = wrap.querySelector('.js-addToCart');
      const link = wrap.querySelector('.js-inCartLink');
      if (!btn || !link) {
        return;
      }

      const container = wrap.closest('.js-productWidget, .js-productCard');
      let vid = btn.getAttribute('data-variation-id') || '';
      if (container) {
        const activeType = container.querySelector(
          '.product-detail-purchase__type--active, .product-card__type--active'
        );
        if (activeType && activeType.getAttribute('data-variation-id')) {
          vid = activeType.getAttribute('data-variation-id');
        }
      }

      const key = String(pid) + '|' + vid;

      if (inCartKeys.has(key)) {
        btn.style.display = 'none';
        link.style.display = '';
      } else {
        btn.style.display = '';
        btn.disabled = false;
        btn.querySelector('.js-addToCart-label').style.display = '';
        btn.querySelector('.js-addToCart-added').style.display = 'none';
        link.style.display = 'none';
      }
    });
  }
  // мини-корзина в шапке

  function initMiniCart() {
    const badge = document.querySelector('.js-cartBadge');
    const miniCart = document.querySelector('.js-miniCart');
    const toggle = document.querySelector('.js-toggleMiniCart');
    const itemsContainer = document.querySelector('.js-miniCartItems');
    const footer = document.querySelector('.js-miniCartFooter');
    const totalEl = document.querySelector('.js-miniCartTotal');

    if (!badge || !miniCart || !toggle) {
      return;
    }

    const updateBadge = (count) => {
      if (count > 0) {
        badge.textContent = count > 99 ? '99+' : count;
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    };

    // Рендер мини-корзины
    const renderMiniCart = (enriched, total) => {
      if (!enriched.length) {
        itemsContainer.innerHTML = '<div class="mini-cart__empty">Корзина пуста</div>';
        footer.style.display = 'none';
        return;
      }

      const html = enriched.map((item) => {
        const typeName = item.type_name ? item.type_name : '';
        const pallet = parseFloat(item.pallet) || 0;
        const billableQty = item.billable_qty || item.quantity;
        const priceText = item.price > 0
          ? `${billableQty} ${item.unit} × ${formatPrice(item.price)}`
          : 'Цена по запросу';

        const palletNote = (pallet > 0 && billableQty !== item.quantity)
          ? `${Math.ceil(item.quantity / pallet)} палл.`
          : '';

        const meta = [typeName, palletNote].filter(Boolean).join(' - ');

        return `
          <div class="mini-cart__item" data-product-id="${item.product_id}" data-variation-id="${item.variation_id || ''}">
            <a href="${item.permalink}" class="mini-cart__title">
              ${item.title}
              ${meta ? `<span>${meta}</span>` : ''}
            </a>
            <div class="mini-cart__price">${priceText}</div>
            <button type="button" class="mini-cart__remove js-miniCartRemove" title="Удалить">✕</button>
          </div>
        `;
      }).join('');

      itemsContainer.innerHTML = html;
      totalEl.textContent = formatPrice(total);
      footer.style.display = '';
    };

    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = miniCart.style.display !== 'none';

      if (isOpen) {
        miniCart.style.display = 'none';
      } else {
        renderMiniCart(window.KG.cart.getEnriched(), window.KG.cart.getTotal());
        miniCart.style.display = '';
      }
    });

    document.addEventListener('click', (e) => {
      if (miniCart.style.display !== 'none' &&
        !e.target.closest('.js-headerCart')) {
        miniCart.style.display = 'none';
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && miniCart.style.display !== 'none') {
        miniCart.style.display = 'none';
      }
    });

    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.js-miniCartRemove');
      if (!removeBtn) return;

      const item = removeBtn.closest('.mini-cart__item');
      const pid = item.getAttribute('data-product-id');
      const vid = item.getAttribute('data-variation-id');

      window.KG.cart.remove(pid, vid);
      window.KG.cart._lastEnriched = window.KG.cart._lastEnriched.filter(
        (i) => !(String(i.product_id) === String(pid) && (i.variation_id || '') === (vid || ''))
      );
      window.KG.cart._lastTotal = window.KG.cart._lastEnriched.reduce((s, i) => s + i.subtotal, 0);

      renderMiniCart(window.KG.cart.getEnriched(), window.KG.cart.getTotal());
    });

    document.addEventListener('cart:updated', (e) => {
      updateBadge(e.detail.count);
      if (miniCart.style.display !== 'none') {
        renderMiniCart(e.detail.enriched, e.detail.total);
      }
    });

    updateBadge(window.KG.cart.getCount());

    if (window.KG.cart.getCount() > 0) {
      window.KG.cart.sync();
    }
  }

  // страница корзины
  function initCartPage() {
    const page = document.querySelector('.js-cartPage');
    if (!page) return;

    const loadingBlock = page.querySelector('.js-cartLoading');
    const emptyBlock = page.querySelector('.js-cartEmpty');
    const contentBlock = page.querySelector('.js-cartContent');
    const tableBody = page.querySelector('.js-cartTableBody');
    const totalEl = page.querySelector('.js-cartPageTotal');
    const staleBlock = page.querySelector('.js-cartStale');
    const reloadBtn = page.querySelector('.js-cartStaleReload');

    let enriched = [];

    const calcTotal = () => enriched.reduce((sum, item) => sum + item.subtotal, 0);

    const updateRow = (pid, vid, item) => {
      const row = tableBody.querySelector(
        `.cart-table__row[data-product-id="${pid}"][data-variation-id="${vid || ''}"]`
      );
      if (!row) return;

      const input = row.querySelector('.js-cartQtyInput');
      if (input && input !== document.activeElement) {
        input.value = item.quantity;
      }

      const subtotalEl = row.querySelector('.cart-table__subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = item.subtotal > 0 ? formatPrice(item.subtotal) : '—';
      }

      const pallet = parseFloat(item.pallet) || 0;
      const billableQty = item.billable_qty || item.quantity;
      const hintEl = row.querySelector('.cart-table__pallet-hint');

      if (pallet > 0 && billableQty !== item.quantity) {
        const palletCount = Math.ceil(item.quantity / pallet);
        const html = `${palletCount} палл. (${billableQty} ${item.unit}) = ${formatPrice(item.subtotal)}`;
        if (hintEl) {
          hintEl.innerHTML = html;
        } else {
          const details = row.querySelector('.cart-table__details');
          details.insertAdjacentHTML('beforeend',
            `<div class="cart-table__pallet-hint">${html}</div>`
          );
        }
      } else if (hintEl) {
        hintEl.remove();
      }
    };

    const renderTable = () => {
      if (!enriched.length) {
        emptyBlock.style.display = '';
        contentBlock.style.display = 'none';
        return;
      }

      emptyBlock.style.display = 'none';
      contentBlock.style.display = '';

      tableBody.innerHTML = enriched.map((item) => {
        const img = item.image
          ? `<img src="${item.image}" alt="${item.title}" loading="lazy">`
          : `<img class="cart-table__photo-placeholder" src="${kgCart.placeholder}" alt="" loading="lazy">`;
        const typeName = item.type_name
          ? `<div class="cart-table__variation">${item.type_name}</div>`
          : '';
        const priceText = item.price > 0 ? formatPrice(item.price) : 'По запросу';
        const unit = item.unit ? `/${item.unit}` : '';

        const pallet = parseFloat(item.pallet) || 0;
        const billableQty = item.billable_qty || item.quantity;
        const palletCount = pallet > 0 ? Math.ceil(item.quantity / pallet) : 0;
        const subtotal = item.subtotal;

        const palletHint = pallet > 0 && billableQty !== item.quantity
          ? `<div class="cart-table__pallet-hint">
             ${palletCount} палл. (${billableQty} ${item.unit}) = ${formatPrice(subtotal)}
           </div>`
          : '';

        return `
        <div class="cart-table__row" data-product-id="${item.product_id}" data-variation-id="${item.variation_id || ''}">
          <div class="cart-table__main">
            <div class="cart-table__photo">${img}</div>
            <div class="cart-table__details">
                <div class="cart-table__title">
                    <a href="${item.permalink}">${item.title}</a>
                </div>
                ${typeName}
                ${palletHint}
            </div>
          </div>
          <div class="cart-table__additional">
            <div class="cart-table__col cart-table__col--price">
              <div class="cart-table__price">
                ${priceText}${unit}
              </div>
            </div>
            <div class="cart-table__col cart-table__col--quantity">
              <div class="cart-qty">
                <button type="button" class="cart-qty__btn js-cartQty" data-action="minus">−</button>
                <input type="text" class="cart-qty__input js-cartQtyInput" value="${item.quantity}">
                <button type="button" class="cart-qty__btn js-cartQty" data-action="plus">+</button>
              </div>
            </div>
            <div class="cart-table__col cart-table__col--total">
              <div class="cart-table__subtotal">
                ${item.subtotal > 0 ? formatPrice(item.subtotal) : '—'}
              </div>
            </div>
          </div>
          <button type="button" class="cart-table__remove js-cartRemove" title="Удалить">✕</button>
        </div>
      `;
      }).join('');

      totalEl.textContent = formatPrice(calcTotal());
    };

    // Локальное обновление enriched без запроса к API
    const updateLocal = (pid, vid, newQty) => {
      if (newQty <= 0) {
        enriched = enriched.filter((item) =>
          !(String(item.product_id) === String(pid) && (item.variation_id || '') === (vid || ''))
        );
        window.KG.cart.remove(pid, vid);
      } else {
        enriched = enriched.map((item) => {
          if (String(item.product_id) === String(pid) && (item.variation_id || '') === (vid || '')) {
            const pallet = parseFloat(item.pallet) || 0;
            let billableQty = newQty;
            if (pallet > 0) {
              billableQty = Math.ceil(newQty / pallet) * pallet;
              billableQty = Math.round(billableQty * 100) / 100;
            }
            return {
              ...item,
              quantity: newQty,
              billable_qty: billableQty,
              subtotal: Math.round(item.price * billableQty * 100) / 100,
            };
          }
          return item;
        });
        window.KG.cart.update(pid, vid, newQty);
      }

      window.KG.cart._lastEnriched = enriched;
      window.KG.cart._lastTotal = calcTotal();

      if (newQty <= 0) {
        renderTable();
      } else {
        const item = enriched.find(
          (i) => String(i.product_id) === String(pid) && (i.variation_id || '') === (vid || '')
        );
        if (item) {
          updateRow(pid, vid, item);
        }
        totalEl.textContent = formatPrice(calcTotal());
      }
    };

    tableBody.addEventListener('click', (e) => {
      const row = e.target.closest('.cart-table__row');
      if (!row) return;

      const pid = row.getAttribute('data-product-id');
      const vid = row.getAttribute('data-variation-id');

      if (e.target.closest('.js-cartRemove')) {
        updateLocal(pid, vid, 0);
        return;
      }

      const qtyBtn = e.target.closest('.js-cartQty');
      if (qtyBtn) {
        const input = row.querySelector('.js-cartQtyInput');
        let val = parseInt(input.value, 10) || 1;
        val = qtyBtn.dataset.action === 'plus' ? val + 1 : Math.max(1, val - 1);
        input.value = val;
        updateLocal(pid, vid, val);
      }
    });

    tableBody.addEventListener('blur', (e) => {
      const input = e.target.closest('.js-cartQtyInput');
      if (!input) return;

      const row = input.closest('.cart-table__row');
      const pid = row.getAttribute('data-product-id');
      const vid = row.getAttribute('data-variation-id');
      let val = parseInt(input.value, 10);

      if (!val || val < 1) val = 1;
      input.value = val;
      updateLocal(pid, vid, val);
    }, true);

    tableBody.addEventListener('input', (e) => {
      const input = e.target.closest('.js-cartQtyInput');
      if (input) input.value = input.value.replace(/\D/g, '');
    });

    // Cross-tab: корзина изменена в другой вкладке
    if (staleBlock && reloadBtn) {
      window.addEventListener('storage', (e) => {
        if (e.key === 'kg_cart') {
          staleBlock.style.display = '';
        }
      });
      reloadBtn.addEventListener('click', () => location.reload());
    }

    // Начальная загрузка
    if (window.KG.cart.getCount() > 0) {
      window.KG.cart.sync().then(() => {
        loadingBlock.style.display = 'none';
        enriched = [...window.KG.cart.getEnriched()];
        renderTable();
      }).catch(() => {
        loadingBlock.style.display = 'none';
        emptyBlock.style.display = '';
      });
    } else {
      loadingBlock.style.display = 'none';
      emptyBlock.style.display = '';
    }
  }

  function initCheckoutPage() {
    const page = document.querySelector('.js-checkoutPage');
    if (!page) return;

    const emptyBlock = page.querySelector('.js-checkoutEmpty');
    const loadingBlock = page.querySelector('.js-checkoutLoading');
    const contentBlock = page.querySelector('.js-checkoutContent');
    const successBlock = page.querySelector('.js-checkoutSuccess');
    const priceChangedBlock = page.querySelector('.js-checkoutPriceChanged');
    const summaryContainer = page.querySelector('.js-checkoutSummary');
    const totalEl = page.querySelector('.js-checkoutTotal');
    const submitBtn = page.querySelector('.js-checkoutSubmit');
    const form = page.querySelector('.js-checkoutForm');
    const addressBlock = page.querySelector('.js-checkoutAddress');

    let isSubmitting = false;

    const renderSummary = (enriched, total) => {
      summaryContainer.innerHTML = enriched.map((item) => {
        const pallet = parseFloat(item.pallet) || 0;
        const billableQty = item.billable_qty || item.quantity;
        const palletNote = (pallet > 0 && billableQty !== item.quantity)
          ? ` (${Math.ceil(item.quantity / pallet)} палл. — ${billableQty} ${item.unit})`
          : '';

        return `
        <div class="checkout-summary__item">
          <div class="checkout-summary__name">
            ${item.title}
            ${item.type_name ? `<span>(${item.type_name})</span>` : ''}
          </div>
          <div class="checkout-summary__group">
            <div class="checkout-summary__qty">${item.quantity} ${item.unit}${palletNote}</div>
            <div class="checkout-summary__subtotal">${item.subtotal > 0 ? formatPrice(item.subtotal) : '—'}</div>
          </div>
        </div>
      `;
      }).join('');

      totalEl.textContent = formatPrice(total);
    };

    // Проверка изменения цен
    const checkPriceChanges = (oldItems, newEnriched) => {
      let changed = false;

      oldItems.forEach((old) => {
        const fresh = newEnriched.find((e) =>
          String(e.product_id) === String(old.product_id) &&
          (e.variation_id || '') === (old.variation_id || '')
        );
        if (!fresh) {
          changed = true; // товар удалён из БД
        }
      });

      // Сравниваем количество — если сервер убрал позиции
      if (newEnriched.length < oldItems.length) {
        changed = true;
      }

      return changed;
    };

    form.addEventListener('change', (e) => {
      if (e.target.name === 'delivery') {
        addressBlock.style.display = e.target.value === 'delivery' ? '' : 'none';

        const addressInput = addressBlock.querySelector('[name="address"]');
        if (e.target.value === 'delivery') {
          addressInput.setAttribute('required', '');
        } else {
          addressInput.removeAttribute('required');
          addressInput.classList.remove('input--error');
        }
      }
    });

    form.addEventListener('focus', (e) => {
      if (e.target.classList.contains('js-checkoutField')) {
        e.target.classList.remove('input--error');
      }
    }, true);

    submitBtn.addEventListener('click', () => {
      if (isSubmitting) {
        return;
      }

      const name = form.querySelector('[name="name"]').value.trim();
      const phone = form.querySelector('[name="phone"]').value.trim();
      const email = form.querySelector('[name="email"]').value.trim();
      const delivery = form.querySelector('[name="delivery"]:checked').value;
      const address = delivery === 'delivery'
        ? form.querySelector('[name="address"]').value.trim()
        : '';
      const payment = form.querySelector('[name="payment"]:checked').value;

      let ok = true;
      let firstError = null;
      form.querySelectorAll('.js-checkoutField').forEach((f) => f.classList.remove('input--error'));

      const markError = (field) => {
        field.classList.add('input--error');
        if (!firstError) firstError = field;
        ok = false;
      };

      if (!name) {
        markError(form.querySelector('[name="name"]'));
      }

      if (!phone || phone.replace(/\D/g, '').length < 11) {
        markError(form.querySelector('[name="phone"]'));
      }

      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        markError(form.querySelector('[name="email"]'));
      }

      if (delivery === 'delivery' && !address) {
        markError(form.querySelector('[name="address"]'));
      }

      if (!ok) {
        firstError.focus();
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      isSubmitting = true;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Отправка...';

      fetch(kgCart.rest_url + 'kg/v1/cart/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': kgCart.nonce,
        },
        body: JSON.stringify({
          items: window.KG.cart.getItems(),
          name,
          phone,
          email,
          delivery,
          address,
          payment,
          page_url: window.location.href,
        }),
      })
        .then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then((resp) => {
          isSubmitting = false;

          if (resp.success) {
            window.KG.cart.clear();
            contentBlock.style.display = 'none';
            priceChangedBlock.style.display = 'none';
            successBlock.style.display = '';
          } else {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Подтвердить заказ';
            alertify.error(resp.message || 'Ошибка оформления заказа');
          }
        })
        .catch((err) => {
          isSubmitting = false;
          submitBtn.disabled = false;
          submitBtn.textContent = 'Подтвердить заказ';
          console.warn('Checkout error', err);
          alertify.error('Ошибка соединения с сервером');
        });
    });

    const staleBlock = page.querySelector('.js-checkoutStale');
    const staleReloadBtn = page.querySelector('.js-checkoutStaleReload');

    window.addEventListener('storage', (e) => {
      if (e.key === 'kg_cart' && staleBlock) {
        staleBlock.style.display = '';
        submitBtn.disabled = true;
      }
    });

    if (staleReloadBtn) {
      staleReloadBtn.addEventListener('click', () => location.reload());
    }

    // Начальная загрузка — sync для актуализации цен
    if (window.KG.cart.getCount() === 0) {
      loadingBlock.style.display = 'none';
      emptyBlock.style.display = '';
      return;
    }

    const oldItems = [...window.KG.cart.getItems()];

    window.KG.cart.sync().then((resp) => {
      loadingBlock.style.display = 'none';

      const enriched = window.KG.cart.getEnriched();

      if (!enriched.length) {
        emptyBlock.style.display = '';
        return;
      }

      // Проверяем изменились ли цены / удалились товары
      if (checkPriceChanges(oldItems, enriched)) {
        priceChangedBlock.style.display = '';
      }

      renderSummary(enriched, window.KG.cart.getTotal());
      contentBlock.style.display = '';
      submitBtn.disabled = false;
    }).catch(() => {
      loadingBlock.style.display = 'none';
      alertify.error('Не удалось загрузить данные заказа');
    });
  }

  window.KG = window.KG || {};
  window.KG.cart = new CartManager();
  window.KG.cart._initCrossTabSync();
  initAddToCartButtons();
  initMiniCart();
  initCartPage();
  initCheckoutPage();
  syncCartButtons();
  document.addEventListener('cart:updated', syncCartButtons);
  document.addEventListener('click', (e) => {
    if (e.target.closest('.js-productVariation')) {
      setTimeout(syncCartButtons, 0);
    }
  });
})();
